import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Content,
  ParcelItemsManagementContainer,
} from './parcel-items-management.styles';
import { useStore } from '../../../stores/store';
import ParcelItemComponent from './parcel-item.component';
import ParcelItemEditForm, {
  ParcelItemFormValues,
} from './parcel-item-form.component';
import Button from '../../../common/button/button.component';
import ParcelProhibitedItemsWithSidebar from '../parcel-prohibited-items-sidebar/parcel-prohibited-items-sidebar.component';
import { ServicesSubSectionHeader } from '../../services/services-subsection/services-subsection.styles';
import TotalValue from './total-value.component';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import { useEditForm } from './edit-form-manager';
import { testIds } from '../../../../test-ids';
import { isCategoryFullyProhibited } from '../../../models/gateway-category';

const NEW_ITEM_EDIT_FORM_ID = 1212121212;

const ParcelItemsManagement = () => {
  const {
    parcelCreationStore: { countryDestination, shipment, isLoadingShipmentApi },
    localizationsStore: { isLanguageChanging },
    parcelItemsAiStore: {
      advanceReviewQueue,
      currentRecognizedGood,
      currentRecognizedGoodIndex,
      discardReviewQueue,
      hasActiveReviewQueue,
      recognizedGoods,
    },
    userStore: { getCurrencySymbol },
  } = useStore();

  const { openedItemId, openEditForm, closeEditForm } = useEditForm();

  const { t } = useTranslation();

  const addingNewItemMode = openedItemId === NEW_ITEM_EDIT_FORM_ID;
  const numOfItemsInShipment = shipment?.items?.length ?? 0;
  const hasFinishedAiReview =
    !hasActiveReviewQueue && currentRecognizedGoodIndex > 0;

  const handleReviewItemComplete = () => {
    const isLastItem = currentRecognizedGoodIndex + 1 >= recognizedGoods.length;

    advanceReviewQueue();

    if (isLastItem) {
      closeEditForm();
    }
  };

  // const totalDeclaredValue = shipment?.items?.at(0)
  //   ? shipment?.items?.map((i) => i.value)?.reduce((prev, cur) => cur + prev)
  //   : 0;

  const totalDeclaredValue = shipment?.total_declared_value ?? 0;

  const currencySymbol = getCurrencySymbol(shipment?.converted_price?.currency);

  const aiFormDefaultValues = useMemo<ParcelItemFormValues | undefined>(() => {
    if (!currentRecognizedGood?.categoryDetails) return undefined;

    const { categoryDetails, recognizedGood } = currentRecognizedGood;
    const requiresImei = categoryDetails.requirements.some(
      (requirement) =>
        requirement.name === 'IMEI1' &&
        requirement.destinationCounties.includes(countryDestination?.code ?? '')
    );

    return {
      foundHsCode: {
        hsCode: categoryDetails.hsCode,
        category_id: categoryDetails.id.toString(),
        description: categoryDetails.name,
        descriptionEn: categoryDetails.nameEn,
        additional_fields: requiresImei ? ['imei'] : undefined,
      },
      category: categoryDetails.name,
      categoryElement: categoryDetails,
      isCategoryProhibited: isCategoryFullyProhibited(
        categoryDetails.prohibitedRules
      ),
      categoryProhibitedRules: categoryDetails.prohibitedRules,
      quantity: recognizedGood.quantity,
      descriptionFree: recognizedGood.detailedDescription,
      is_new: !recognizedGood.used,
    };
  }, [countryDestination?.code, currentRecognizedGood]);

  return (
    <ParcelItemsManagementContainer
      data-testid={testIds.parceCreation.parcelContent.contentBlock}>
      <ServicesSubSectionHeader>
        {t('parcel_content_title')}
        <ParcelProhibitedItemsWithSidebar />
      </ServicesSubSectionHeader>
      <Content>
        {numOfItemsInShipment > 0 && (
          <FlexContainer
            $gap='1.6rem'
            $direction='column'
            $mobDirection='column'
            $align='start'>
            {shipment?.items
              ?.slice()
              .sort((a, b) => a.id - b.id)
              .map((item) => (
                <ParcelItemComponent
                  shouldShowDeleteButton={numOfItemsInShipment > 1}
                  currencySymbol={currencySymbol}
                  key={item.id}
                  item={item}
                  disableActions={hasActiveReviewQueue}
                  withSeparator
                />
              ))}
          </FlexContainer>
        )}
        {(numOfItemsInShipment > 0 || hasFinishedAiReview) &&
          !addingNewItemMode &&
          !hasActiveReviewQueue && (
            <div className='container-add-new-item'>
              <Button
                id='add_new_item'
                appearance='secondary'
                size='large'
                hideLinkUnderline
                $pb='0'
                $pt='0'
                onClick={() => {
                  openEditForm(NEW_ITEM_EDIT_FORM_ID);
                }}
                icon={{ glyph: 'plus' }}
                disabled={isLoadingShipmentApi || isLanguageChanging}>
                {t('add_new_item')}
              </Button>
            </div>
          )}
        {hasActiveReviewQueue &&
          currentRecognizedGood &&
          aiFormDefaultValues && (
            <ParcelItemEditForm
              key={currentRecognizedGoodIndex}
              defaultValues={aiFormDefaultValues}
              displayCancelButton
              currencySymbol={currencySymbol}
              allowUnmodifiedSubmit
              onCancel={handleReviewItemComplete}
              onDiscardAiReview={discardReviewQueue}
              onSuccess={handleReviewItemComplete}
              aiReviewProgress={{
                current: currentRecognizedGoodIndex + 1,
                total: recognizedGoods.length,
              }}
            />
          )}
        {!hasActiveReviewQueue &&
          ((numOfItemsInShipment === 0 && !hasFinishedAiReview) ||
            addingNewItemMode) && (
            <ParcelItemEditForm
              displayCancelButton={numOfItemsInShipment > 0}
              currencySymbol={currencySymbol}
            />
          )}
        {numOfItemsInShipment > 0 && (
          <TotalValue
            totalValue={totalDeclaredValue ?? 0}
            currencySymbol={currencySymbol}
          />
        )}
      </Content>
    </ParcelItemsManagementContainer>
  );
};

export default observer(ParcelItemsManagement);
